import React, {
  FunctionComponent,
  useEffect,
  useState,
  useCallback,
  ChangeEvent,
  KeyboardEvent as ReactKeyboardEvent,
  useRef,
} from 'react';
import './EditRegionConfigModal.scss';
import Fetch from '../../../../../utils/fetch';
import { settings } from '../../../../../settings';
import { capitalize } from '../../../../../utils/strings';
import { InputComponent } from '../../../../../ReusableComponents/Input/InputComponent';
import { ButtonComponent } from '../../../../../ReusableComponents/Button/ButtonComponent';
import { CapacityPerWeekday } from '../../../../../types/capacityTypes';
import { DayOfWeek } from '../../../../../types/javaTypes';

interface UpdateResult {
  success: boolean;
  errorMessage: string;
}

interface Props {
  regionName: string;
  maxDailyCapacity: number | null;
  maxDeliveriesPerWeekDay?: CapacityPerWeekday;
  onCloseModal: () => void;
  toggleToastMessage: (isToastVisible: boolean) => void;
  setToastConfigObject: (configObject: {
    text: string;
    type: 'success' | 'error';
  }) => void;
  updateRegionalCapacity: () => void;
  toggleLoader: (isVisible: boolean) => void;
}

type DayCapacityErrors = {
  [key in `${DayOfWeek}-${'capacity'}`]?: boolean;
};

const displayedWeekDays: DayOfWeek[] = [
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
  DayOfWeek.Sunday,
];

const initialWeekdayCapacities = (
  maxDeliveriesPerDay?: CapacityPerWeekday
): CapacityPerWeekday => {
  const template = {
    [DayOfWeek.Monday]: '',
    [DayOfWeek.Tuesday]: '',
    [DayOfWeek.Wednesday]: '',
    [DayOfWeek.Thursday]: '',
    [DayOfWeek.Friday]: '',
    [DayOfWeek.Saturday]: '',
    [DayOfWeek.Sunday]: '',
  };
  if (maxDeliveriesPerDay) {
    Object.keys(maxDeliveriesPerDay).forEach(key => {
      if (maxDeliveriesPerDay[key] !== null) {
        template[key] = maxDeliveriesPerDay[key];
      }
    });
  }
  return template;
};

const EditRegionConfigModal: FunctionComponent<Props> = props => {
  const {
    regionName,
    onCloseModal,
    setToastConfigObject,
    toggleToastMessage,
    updateRegionalCapacity,
    maxDailyCapacity,
    maxDeliveriesPerWeekDay,
    toggleLoader,
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [maxDailyCapacityState, setMaxDailyCapacityState] = useState<string>(
    (maxDailyCapacity !== null && maxDailyCapacity.toString()) || ''
  );
  const [capPerWeekDayState, setCapPerWeekDayState] =
    useState<CapacityPerWeekday | null>(
      initialWeekdayCapacities(maxDeliveriesPerWeekDay) || null
    );
  const [dayCapacityErrors, setDayCapacityErrors] = useState<DayCapacityErrors>(
    {}
  );
  const [inputErrorMessage, setInputErrorMessage] = useState<string>('');
  const escFunction = useCallback(
    (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onCloseModal();
      }
    },
    [onCloseModal]
  );
  const onChangeMaxDailyCapacity = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      if (inputErrorMessage) {
        setInputErrorMessage('');
      }
      const { value } = event.currentTarget;
      if (value.trim() === '') {
        setMaxDailyCapacityState(value);
        setInputErrorMessage('Provided value cannot be empty');
        return;
      }
      const isNum = /^\d+$/.test(value);
      if (!isNum) {
        setMaxDailyCapacityState(value);
        setInputErrorMessage('Provided value is not valid');
        return;
      }
      setMaxDailyCapacityState(value);
    },
    [inputErrorMessage]
  );
  const onChangeCapacityPerWeekday = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const { value, name } = event.target;
      const [day] = name.split('-');
      const isValid = value.trim() === '' || /^\d+$/.test(value);
      setDayCapacityErrors(previousErrors => ({
        ...previousErrors,
        [name]: !isValid,
      }));
      setCapPerWeekDayState(currentCapPerWeekday => ({
        ...currentCapPerWeekday,
        [day]: value.trim(),
      }));
    },
    []
  );
  const postRegionCapacityLimit = useCallback(
    async (region: string): Promise<UpdateResult> => {
      const maxDeliveriesPerDayFiltered: Partial<CapacityPerWeekday> = {};
      Object.keys(capPerWeekDayState).forEach(key => {
        if (capPerWeekDayState[key] !== '') {
          maxDeliveriesPerDayFiltered[key] = capPerWeekDayState[key];
        }
      });
      try {
        const res = await Fetch.put({
          url: `${settings.newApiUrl}/geography/region/capacity-limit`,
          body: {
            regionName: region,
            maxDailyCapacity: +maxDailyCapacityState,
            maxDeliveriesPerWeekDay: maxDeliveriesPerDayFiltered,
          },
        });
        if (res.ok) {
          return {
            success: true,
            errorMessage: '',
          };
        } else if (res.status === 422) {
          return {
            success: false,
            errorMessage: (await res.json()).map(
              (resJson: { message: any }) => resJson.message
            ),
          };
        } else {
          return {
            success: false,
            errorMessage: 'The region capacity could not be updated',
          };
        }
      } catch (e) {
        console.error('Error updating region capacity', e);
        return {
          success: false,
          errorMessage: 'The region capacity could not be updated',
        };
      }
    },
    [maxDailyCapacityState, capPerWeekDayState]
  );

  const save = useCallback(async (): Promise<void> => {
    const result = await postRegionCapacityLimit(regionName);
    if (result.success) {
      setToastConfigObject({
        text: `New capacity for ${capitalize(
          regionName
        )} was set to ${maxDailyCapacityState}`,
        type: 'success',
      });
      toggleLoader(true);
      toggleToastMessage(true);
      updateRegionalCapacity();
      onCloseModal();
    } else {
      setToastConfigObject({ text: result.errorMessage, type: 'error' });
      toggleToastMessage(true);
    }
  }, [
    maxDailyCapacityState,
    onCloseModal,
    postRegionCapacityLimit,
    regionName,
    setToastConfigObject,
    toggleLoader,
    toggleToastMessage,
    updateRegionalCapacity,
  ]);
  const enterKeyPressed = useCallback(
    async (event: ReactKeyboardEvent<HTMLDivElement>): Promise<void> => {
      if (
        event.key === 'Enter' &&
        !inputErrorMessage &&
        !Object.keys(dayCapacityErrors).some(key => dayCapacityErrors[key])
      ) {
        await save();
      }
    },
    [dayCapacityErrors, inputErrorMessage, save]
  );
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    document.addEventListener('keydown', escFunction, false);
    return () => {
      document.addEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);
  return (
    <div className="edit-region-config-modal__container">
      <div className="edit-region-config-modal__content">
        <div className="edit-region-config-modal__content-row">
          <label
            className="edit-region-config-modal__label-strong"
            htmlFor="edit-region-capacity"
          >
            Max deliveries per day (default)
          </label>
          <InputComponent
            inputRef={inputRef}
            className="edit-region-config-modal__input"
            id="edit-region-capacity"
            label=""
            name="edit-region-capacity"
            variant="outlined"
            fullWidth
            error={!!inputErrorMessage}
            helperText={inputErrorMessage}
            onChange={onChangeMaxDailyCapacity}
            value={maxDailyCapacityState}
            onKeyPress={enterKeyPressed}
          />
        </div>
        <div>Weekday capacities will override default:</div>
        {displayedWeekDays.map(day => (
          <div
            className="edit-region-config-modal__content-row"
            key={`${day.toLowerCase()}MaxCapacity`}
          >
            <label
              className="edit-region-config-modal__weekday-label"
              htmlFor={`${day.toLowerCase()}CapacityInput`}
            >
              {capitalize(day)}
            </label>
            <InputComponent
              id={`${day.toLowerCase()}CapacityInput`}
              label=""
              size="small"
              variant="outlined"
              margin="dense"
              name={`${day}-capacity`}
              value={capPerWeekDayState !== null ? capPerWeekDayState[day] : ''}
              onChange={onChangeCapacityPerWeekday}
              placeholder={maxDailyCapacityState}
              error={dayCapacityErrors[`${day}-capacity`]}
              onKeyPress={enterKeyPressed}
            />
          </div>
        ))}
      </div>
      <div className="edit-region-config-modal__button-section">
        <ButtonComponent
          text="Cancel"
          variant="outlined"
          onClick={onCloseModal}
        />
        <ButtonComponent
          text="Save"
          onClick={save}
          disabled={
            !!inputErrorMessage ||
            Object.keys(dayCapacityErrors).some(key => dayCapacityErrors[key])
          }
        />
      </div>
    </div>
  );
};
export { EditRegionConfigModal };
